import { Lock, Key } from 'lucide-react'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { CreateIdpProviderDto } from '../../../types/types'
import styles from '../SSOConfiguration.module.css'

interface OktaProviderFormProps {
  providerForm: CreateIdpProviderDto
  onFieldChange: (field: keyof CreateIdpProviderDto, value: string) => void
  onConfigChange: (key: string, value: string) => void
  hasClientSecret?: boolean
}

export default function OktaProviderForm({
  providerForm,
  onFieldChange,
  onConfigChange,
  hasClientSecret
}: OktaProviderFormProps) {
  return (
    <div className={styles.providerFormGrid}>
      <div className={styles.formField}>
        <SecureInput
          fieldType="name"
          label="Name *"
          value={providerForm.name}
          onChange={(value) => onFieldChange('name', value)}
          placeholder="e.g. Okta Production"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Description"
          value={providerForm.description || ''}
          onChange={(value) => onFieldChange('description', value)}
          placeholder="Optional description"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Okta Domain *"
          value={providerForm.config?.oktaDomain || ''}
          onChange={(value) => onConfigChange('oktaDomain', value)}
          placeholder="your-company.okta.com"
          className={styles.input}
          description="Your Okta domain (without https://)"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="key"
          label="Client ID *"
          icon={<Key size={16} />}
          value={providerForm.clientId || ''}
          onChange={(value) => onFieldChange('clientId', value)}
          placeholder="Application Client ID from Okta"
          className={styles.input}
          description="OAuth2 Client ID from your Okta application"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="password"
          label="Client Secret *"
          icon={<Lock size={16} />}
          value={providerForm.clientSecret || ''}
          onChange={(value) => onFieldChange('clientSecret', value)}
          placeholder={hasClientSecret ? 'Secret is set — leave blank to keep it' : 'Application Client Secret from Okta'}
          className={styles.input}
          skipPasswordValidation
          description={hasClientSecret
            ? 'A secret is already configured. Leave blank to keep it, or enter a new value to replace it.'
            : 'OAuth2 Client Secret from your Okta application'}
          autoComplete="new-password"
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="url"
          label="Issuer URL"
          value={providerForm.issuerUrl || ''}
          onChange={(value) => onFieldChange('issuerUrl', value)}
          placeholder="https://your-company.okta.com/oauth2/default"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Authorization URL"
          value={providerForm.authorizationUrl || ''}
          onChange={(value) => onFieldChange('authorizationUrl', value)}
          placeholder="/oauth2/v1/authorize"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Token URL"
          value={providerForm.tokenUrl || ''}
          onChange={(value) => onFieldChange('tokenUrl', value)}
          placeholder="/oauth2/v1/token"
          className={styles.input}
        />
      </div>

      <div className={styles.formField}>
        <SecureInput
          fieldType="text"
          label="Scopes"
          value={providerForm.config?.scope || ''}
          onChange={(value) => onConfigChange('scope', value)}
          placeholder="openid email profile groups"
          className={styles.input}
          description="OAuth2 scopes to request (space-separated)"
          autoComplete="off"
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.fieldLabel}>Include Groups</label>
        <select
          value={providerForm.config?.includeGroups || 'false'}
          onChange={(e) => onConfigChange('includeGroups', e.target.value)}
          className={styles.select}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
        <p className={styles.fieldDescription}>Whether to fetch user group memberships</p>
      </div>
    </div>
  )
}
